import { createFileRoute } from "@tanstack/react-router";
import { PortalShell } from "@/components/layout/PortalShell";
import { ADMIN_NAV } from "@/lib/admin-nav";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { adminListPendingFulfillment, adminMarkDelivered } from "@/lib/store.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/store/orders")({
  component: AdminOrdersPage,
});

function AdminOrdersPage() {
  const qc = useQueryClient();
  const fetchPending = useServerFn(adminListPendingFulfillment);
  const markDelivered = useServerFn(adminMarkDelivered);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "store", "pending"],
    queryFn: () => fetchPending(),
  });

  async function onDeliver(orderId: string) {
    try {
      await markDelivered({ data: { orderId } });
      toast.success("Marked as delivered.");
      qc.invalidateQueries({ queryKey: ["admin", "store", "pending"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  }

  return (
    <PortalShell nav={ADMIN_NAV} title="Fulfillment Queue">
      <Eyebrow>Awaiting fulfillment</Eyebrow>
      <h1 className="mt-2 font-serif text-3xl text-navy-900">24-hour SLA</h1>
      {isLoading ? (
        <p className="mt-8 font-sans text-charcoal-500">Loading…</p>
      ) : !data || data.length === 0 ? (
        <p className="mt-8 font-sans text-charcoal-500">No pending orders.</p>
      ) : (
        <table className="mt-8 w-full border-collapse text-left font-sans text-sm">
          <thead>
            <tr className="border-b border-bronze-600/30 text-xs uppercase tracking-[0.22em] text-charcoal-500">
              <th className="py-3">Order</th><th>Buyer</th><th>Status</th><th>Due</th><th />
            </tr>
          </thead>
          <tbody>
            {data.map((o) => (
              <tr key={o.id} className="border-b border-bronze-600/10">
                <td className="py-4 font-mono text-xs">{o.id.slice(0, 8)}</td>
                <td>{o.guest_email ?? o.user_id ?? "—"}</td>
                <td>{o.fulfillment_status}</td>
                <td>{o.fulfillment_due_at ? new Date(o.fulfillment_due_at).toLocaleString() : "—"}</td>
                <td className="text-right">
                  <PrimaryButton onClick={() => onDeliver(o.id)}>Mark delivered</PrimaryButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </PortalShell>
  );
}